import type { components } from "../../../api/generated/vision-api";
import { ApiError } from "../../../api/problem";
import { formatNumber } from "../../../i18n/format";

type T = (key: string, options?: Record<string, unknown>) => string;

export type DevicePlan = components["schemas"]["DevicePlan"];
export type Device = components["schemas"]["Device"];
export type UpgradeResult = components["schemas"]["UpgradeRequestResult"];

/** The longest note the API takes (device-plans DP-20). */
export const NOTE_MAX = 500;

/** The plans whose texts the app knows (DP-01); another plan shows its server name and no list. */
export const KNOWN_PLANS = ["free", "basic", "plus", "pro", "enterprise"] as const;

/** What each plan adds (P-64), as i18n keys under plans.includes. */
const INCLUDES: Record<string, string[]> = {
  free: ["live", "control", "page", "pushAlarms"],
  basic: ["history", "dashboards", "exports", "deviceRules"],
  plus: ["serverAutomations"],
  pro: ["forecasts", "statistics"],
  enterprise: ["contract"],
};

/** The features an organisation's own Enterprise plan can switch (plan-limits-2 PLR-10), as plans.includes keys. */
const FEATURE_KEYS: [feature: string, key: string][] = [
  ["history", "history"],
  ["dashboards", "dashboards"],
  ["exports", "exports"],
  ["device_rules", "deviceRules"],
  ["server_automations", "serverAutomations"],
  ["forecasts", "forecasts"],
  ["statistics", "statistics"],
  ["mqtt_feed", "mqttFeed"],
];

export function planName(id: string, t: T, serverName?: string): string {
  return t(`sharing.plan.${id}`, { defaultValue: serverName ?? id });
}

/** The card's title: "Enterprise · <name>" for an organisation's own Enterprise plan (PLR-10), else the plan's name. */
export function planTitle(p: Pick<DevicePlan, "id" | "name" | "display_name">, t: T): string {
  return p.display_name || planName(p.id, t, p.name);
}

/**
 * The list of what a plan adds over the one below it, as translated lines. An organisation's own Enterprise plan
 * (PLR-14) lists everything it includes instead, from its feature switches, so a feature switched off is not there.
 */
export function planIncludes(p: Pick<DevicePlan, "id" | "features" | "custom">, t: T): string[] {
  if (p.custom) {
    const on = FEATURE_KEYS.filter(([f]) => p.features?.[f] === true).map(([, k]) => k);
    return [...(INCLUDES.free ?? []), ...on, "agreed"].map((k) => t(`plans.includes.${k}`));
  }
  return (
    (INCLUDES[p.id] ?? [])
      // Forecasts and statistics only where the product has them (DP-03, pro-statistics PST-02).
      .filter((k) => (k !== "forecasts" && k !== "statistics") || p.features?.[k] === true)
      .map((k) => t(`plans.includes.${k}`))
  );
}

/** "Free", "Price on request" or "2,00 € / month" (DP-02, DP-04): per device per month, VAT excluded. */
export function planPrice(p: Pick<DevicePlan, "id" | "price">, lang: string, t: T): string {
  if (!p.price) return t("plans.price.onRequest");
  if (p.price.month_cents === 0) return t("plans.price.free");
  const amount = formatNumber(p.price.month_cents / 100, lang, { style: "currency", currency: p.price.currency });
  return t("plans.price.perMonth", { amount });
}

/** "Live values only", "1 month", "1 year", "3 years", "10 years" (DP-01). */
export function planHistory(days: number | null | undefined, t: T): string {
  if (days == null) return t("plans.history.contract");
  if (days <= 0) return t("plans.history.none");
  if (days < 365) return t("plans.history.months", { count: Math.max(1, Math.round(days / 30)) });
  return t("plans.history.years", { count: Math.round(days / 365) });
}

/** The plan below this one in the API's list (plan-limits PL-01): the next lower sort, or none for the lowest. */
export function planBelow(plans: Pick<DevicePlan, "id" | "name" | "sort">[], p: Pick<DevicePlan, "sort">) {
  return plans
    .filter((o) => o.sort < p.sort)
    .reduce<Pick<DevicePlan, "id" | "name" | "sort"> | undefined>(
      (best, o) => (!best || o.sort > best.sort ? o : best),
      undefined,
    );
}

/** "up to 3", "not included" or "no limit" (plan-limits PL-02). */
export function planLimit(n: number | null | undefined, t: T): string {
  if (n == null) return t("plans.limit.unlimited");
  if (n <= 0) return t("plans.limit.none");
  return t("plans.limit.upTo", { count: n });
}

/** "Your plan shows 1 month of history." (plan-limits PL-41); empty when the plan sets no window. */
export function historyLimitText(days: number | null | undefined, t: T): string {
  if (days == null || days <= 0) return "";
  return t("plans.historyLimit", { period: planHistory(days, t) });
}

/**
 * The history a chart over these devices can show (plan-limits PL-41): the shortest plan window among them, in days;
 * undefined when no device has a window (not loaded, live only, or agreed per contract).
 */
export function shownHistoryDays(devices: (Pick<Device, "plan_limits"> | undefined)[]): number | undefined {
  const days = devices
    .map((d) => d?.plan_limits?.history_days)
    .filter((n): n is number => typeof n === "number" && n > 0);
  return days.length ? Math.min(...days) : undefined;
}

/** "every hour", "every 20 minutes" (DP-06); empty when the plan sets none. */
export function planInterval(seconds: number | null | undefined, t: T): string {
  if (!seconds) return "";
  if (seconds % 3600 === 0) return t("plans.interval.hours", { count: seconds / 3600 });
  return t("plans.interval.minutes", { count: Math.round(seconds / 60) });
}

/** What the Plan card says the plan comes from (DP-10, DP-14, DP-30). */
export function planSourceText(
  d: Pick<Device, "plan_source" | "plan_until" | "plan_free" | "plan_managed_by">,
  day: (iso: string) => string,
  t: T,
): string {
  if (d.plan_managed_by) return t("plans.source.managed", { partner: d.plan_managed_by });
  if (d.plan_free && d.plan_until) return t("plans.source.freeUntil", { date: day(d.plan_until) });
  if (d.plan_source === "subscription")
    return d.plan_until
      ? t("plans.source.subscriptionUntil", { date: day(d.plan_until) })
      : t("plans.source.subscription");
  if (d.plan_source === "organisation") return t("plans.source.organisation");
  return t("plans.source.free");
}

/** Whether a plan is below the device's current one: its button and confirm say Downgrade (DP-41). */
export function isLower(p: Pick<DevicePlan, "sort">, current: Pick<DevicePlan, "sort"> | undefined): boolean {
  return !!current && p.sort < current.sort;
}

/** Why a plan change request was refused, in plain words. */
export function upgradeError(e: unknown, t: T): string {
  if (e instanceof UntrustedCheckout) return t("plans.error.checkout");
  if (!(e instanceof ApiError)) return t("plans.error.unknown");
  switch (e.code) {
    case "plan_is_current":
    case "plan_managed_by_partner":
      return t(`plans.error.${e.code}`);
    case "rate_limited":
      return t("plans.error.rate_limited", { minutes: e.retryAfterMinutes ?? 60 });
  }
  if (e.status === 403) return t("plans.error.forbidden");
  return e.message || t("plans.error.unknown");
}

/**
 * What to do with the API's answer (DP-23): a request was sent, or (later, with payment) a checkout page opens. Apps
 * written now handle both, so no release is needed when payment arrives.
 */
export function nextStep(r: UpgradeResult): { kind: "sent"; replyTo?: string } | { kind: "checkout"; url: string } {
  if (r.next.kind === "checkout" && r.next.url) {
    if (!trustedCheckout(r.next.url)) throw new UntrustedCheckout(r.next.url);
    return { kind: "checkout", url: r.next.url };
  }
  return { kind: "sent", replyTo: r.next.reply_to };
}

/**
 * Hosts a checkout page may be on: Creavision's own domain. The payment provider's host is added here when payment is
 * chosen (FR-PLN-05), so a wrong or tampered answer never sends a customer to another site to pay.
 */
export const CHECKOUT_DOMAINS = ["creavisiontechnology.com"];

/** Whether the app may open a checkout URL: https, on a host of CHECKOUT_DOMAINS or below it. */
export function trustedCheckout(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (u.protocol !== "https:" || u.username || u.password) return false;
  const host = u.hostname.toLowerCase();
  return CHECKOUT_DOMAINS.some((d) => host === d || host.endsWith("." + d));
}

/** A checkout URL the app will not open. */
export class UntrustedCheckout extends Error {
  constructor(url: string) {
    super(`checkout URL not on a trusted host: ${url}`);
    this.name = "UntrustedCheckout";
  }
}
